import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  FiX,
  FiAlertCircle,
  FiBook,
  FiCpu,
  FiUsers,
  FiTarget,
  FiLayers,
  FiCalendar,
  FiAward,
  FiChevronDown,
  FiChevronUp,
  FiCode,
  FiCreditCard,
  FiGlobe,
  FiCheckCircle,
  FiLock,
  FiBookOpen,
  FiClock,
  FiDatabase,
  FiRefreshCw,
  FiDownload,
  FiShare2,
  FiCopy,
  FiSearch,
  FiPackage,
  FiList
} from 'react-icons/fi';
import api from '../../api';

const ViewMappedSubjects = ({ 
  tenantId, 
  programId, 
  programName,
  programTypeId, 
  programTypeName,
  academicYearId, 
  academicYearName,
  semesterId,
  termId,
  mapping,
  onClose,
  isReadOnly = false
}) => {
  const [fetchedMapping, setMapping] = useState(mapping);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedDepartments, setExpandedDepartments] = useState(new Set());
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [academicYearDetails, setAcademicYearDetails] = useState(null);

  // Data Fetching
  const fetchAcademicYearDetails = useCallback(async () => {
    if (!academicYearId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/academic-year/${academicYearId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAcademicYearDetails(res.data);
      
      if (res.data.isSemesterBased) {
        // Fetch semesters
        const semRes = await api.get(`/api/classManager/semesters/${academicYearId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAvailableSemesters(semRes.data.semesters || []);
        setAvailableTerms([]);
      } else {
        // Fetch terms
        const termRes = await api.get(`/api/academic-year/${academicYearId}/terms?tenantId=${tenantId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAvailableTerms(termRes.data?.terms || []);
        setAvailableSemesters([]);
      }
    } catch (err) {
      console.error('Error fetching academic year details:', err);
    }
  }, [academicYearId, tenantId]);

  const getSemesterName = useCallback((semesterIdToCheck) => {
    if (!semesterIdToCheck) return '';
    if (typeof semesterIdToCheck === 'object') {
      return semesterIdToCheck.semesterName || `Semester ${semesterIdToCheck.semesterNumber}`;
    }
    const foundSemester = availableSemesters.find(sem => sem._id === semesterIdToCheck);
    return foundSemester ? (foundSemester.semesterName || `Semester ${foundSemester.semesterNumber}`) : '';
  }, [availableSemesters]);

  const getTermName = useCallback((termIdToCheck) => {
    if (!termIdToCheck) return '';
    if (typeof termIdToCheck === 'object') {
      return termIdToCheck.termLabel || `Term ${termIdToCheck.termNumber}`;
    }
    const foundTerm = availableTerms.find(term => term._id === termIdToCheck);
    return foundTerm ? (foundTerm.termLabel || `Term ${foundTerm.termNumber}`) : '';
  }, [availableTerms]);

  const fetchMapping = useCallback(async () => {
    if (!tenantId || !programId || !academicYearId) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      await fetchAcademicYearDetails();

      const params = { tenantId, programId, programTypeId, academicYearId };
      
      // Add semesterId OR termId based on academic year type
      if (academicYearDetails?.isSemesterBased) {
        if (!semesterId) {
          setError('Semester ID is required for semester-based academic year');
          setLoading(false);
          return;
        }
        params.semesterId = semesterId;
      } else {
        if (!termId) {
          setError('Term ID is required for term-based academic year');
          setLoading(false);
          return;
        }
        params.termId = termId;
      }

      const response = await api.get('/api/program-subject-mappings/grouped', { params });
      
      if (response.data.mapping) {
        setMapping(response.data.mapping);
        const allDeptIndexes = new Set(response.data.mapping.departmentSubjects?.map((_, index) => index));
        setExpandedDepartments(allDeptIndexes);
      } else {
        setError('No mapping found for the selected context');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No subject mapping found.');
      } else {
        setError(err.response?.data?.message || 'Failed to load details');
      }
    } finally {
      setLoading(false);
    }
  }, [
    tenantId, programId, programTypeId, academicYearId, semesterId, termId, 
    academicYearDetails, fetchAcademicYearDetails
  ]);

  useEffect(() => {
    if (!mapping) {
      fetchMapping();
    } else {
      setLoading(false);
      const allDeptIndexes = new Set(mapping.departmentSubjects?.map((_, index) => index));
      setExpandedDepartments(allDeptIndexes);
      if (mapping.academicYearId) fetchAcademicYearDetails();
    }
  }, [tenantId, programId, programTypeId, academicYearId, semesterId, termId, mapping, fetchMapping, fetchAcademicYearDetails]);

  useEffect(() => {
    if (mapping) setMapping(mapping);
  }, [mapping]);

  // Helper Logic
  const toggleDepartment = (index) => {
    const newExpanded = new Set(expandedDepartments);
    if (newExpanded.has(index)) newExpanded.delete(index);
    else newExpanded.add(index);
    setExpandedDepartments(newExpanded);
  };

  const toggleAllDepartments = () => {
    if (expandedDepartments.size === fetchedMapping?.departmentSubjects?.length) {
      setExpandedDepartments(new Set());
    } else {
      const allIndexes = new Set(fetchedMapping?.departmentSubjects?.map((_, i) => i));
      setExpandedDepartments(allIndexes);
    }
  };

  // Get subject data with enhanced custom type detection
  const getSubjectData = (subject) => {
    const data = subject.subjectId || subject;
    
    const isCustomSubject = data.subjectType === 'custom' || 
                           (data.customSubjectType && data.customSubjectType.trim() !== '');
    const customSubjectType = isCustomSubject ? 
                             (data.customSubjectType?.trim() || data.name || 'Custom') : null;
    
    return {
      ...data,
      isCustomSubject,
      customSubjectType,
      hasCustomType: isCustomSubject,
      isLab: data.isLab || data.subjectType === 'lab',
      isElective: data.isElective,
      isLanguage: data.isLanguage || data.subjectType === 'language' || data.languageType,
      isProject: data.isProject || data.subjectType === 'project',
      displayType: isCustomSubject ? customSubjectType : 
                  (data.isProject || data.subjectType === 'project') ? 'Project' :
                  (data.isLab || data.subjectType === 'lab') ? 'Lab' :
                  (data.isElective) ? 'Elective' :
                  (data.isLanguage || data.subjectType === 'language' || data.languageType) ? 'Language' : 'Core'
    };
  };

  const getSubjectTypeInfo = (subject) => {
    const data = getSubjectData(subject);
    
    if (data.isCustomSubject && data.customSubjectType) {
      return { 
        icon: FiPackage, 
        label: data.customSubjectType,
        badgeClass: 'bg-gradient-to-r from-teal-50 to-cyan-50 text-teal-700 border border-teal-200',
        color: 'teal',
        gradient: 'from-teal-500 to-cyan-500'
      };
    }

    if (data.isProject) {
      return { 
        icon: FiTarget, 
        label: 'Project', 
        badgeClass: 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200',
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500'
      };
    }

    if (data.isLab) {
      return { 
        icon: FiCpu, 
        label: 'Lab', 
        badgeClass: 'bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-700 border border-violet-200',
        color: 'violet',
        gradient: 'from-violet-500 to-indigo-500'
      };
    }
    
    if (data.isLanguage) {
      return { 
        icon: FiGlobe, 
        label: data.languageType || 'Language', 
        badgeClass: 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 border border-cyan-200',
        color: 'cyan',
        gradient: 'from-cyan-500 to-blue-500'
      };
    }

    if (data.isElective) {
      return { 
        icon: FiUsers, 
        label: data.electiveType || 'Elective', 
        badgeClass: 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200',
        color: 'amber',
        gradient: 'from-amber-500 to-orange-500'
      };
    }

    return { 
      icon: FiBook, 
      label: 'Core', 
      badgeClass: 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200',
      color: 'blue',
      gradient: 'from-blue-500 to-indigo-500'
    };
  };

  // Enhanced Context Resolution
  const resolveProgramName = useMemo(() => {
    if (programName && programName.trim()) return programName;
    if (fetchedMapping?.programId?.name) return fetchedMapping.programId.name;
    if (typeof fetchedMapping?.programId === 'object' && fetchedMapping.programId.name) 
      return fetchedMapping.programId.name;
    if (fetchedMapping?.programId)
      return `Program (${fetchedMapping.programId})`;
    return 'Program Details';
  }, [programName, fetchedMapping]);

  const resolveProgramType = useMemo(() => {
    if (programTypeName && programTypeName.trim()) return programTypeName;
    if (fetchedMapping?.programTypeId?.name) return fetchedMapping.programTypeId.name;
    if (typeof fetchedMapping?.programTypeId === 'object' && fetchedMapping.programTypeId.name) 
      return fetchedMapping.programTypeId.name;
    if (fetchedMapping?.programTypeId)
      return `Type (${fetchedMapping.programTypeId})`;
    return 'Program Type';
  }, [programTypeName, fetchedMapping]);

  // Format date as "Created at" date
  const formatCreatedDate = useMemo(() => {
    if (!fetchedMapping?.createdAt) return '';
    const date = new Date(fetchedMapping.createdAt);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }, [fetchedMapping]);

  // Get context (semester or term) display text
  const contextDisplay = useMemo(() => {
    const isSemesterBased = academicYearDetails?.isSemesterBased;
    
    if (isSemesterBased) {
      const semId = semesterId || fetchedMapping?.semesterId;
      if (!semId) return null;
      const semesterName = getSemesterName(semId);
      return {
        type: 'Semester',
        name: semesterName,
        icon: FiCalendar,
        color: 'blue',
        gradient: 'from-blue-500 to-cyan-500',
        badgeClass: 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
      };
    } else {
      const termIdToUse = termId || fetchedMapping?.termId;
      if (!termIdToUse) return null;
      const termName = getTermName(termIdToUse);
      return {
        type: 'Term',
        name: termName,
        icon: FiList,
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500',
        badgeClass: 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700'
      };
    }
  }, [academicYearDetails, semesterId, fetchedMapping, termId, getSemesterName, getTermName]);

  // Dynamic Stats Calculation
  const { stats, statCards, mappingSummaryItems } = useMemo(() => {
    if (!fetchedMapping?.departmentSubjects) return { stats: {}, statCards: [], mappingSummaryItems: [] };
    
    const stats = {
      total: 0,
      credits: fetchedMapping.totalCredits || 0,
      core: 0,
      labs: 0,
      electives: 0,
      projects: 0,
      languages: 0,
      customTypes: {},
      allCustomTypes: new Map()
    };

    fetchedMapping.departmentSubjects.forEach(dept => {
      dept.subjects?.forEach(sub => {
        const data = getSubjectData(sub);
        stats.total++;
        
        const subjectCredits = sub.credits || data.credits;

        if (!fetchedMapping.totalCredits) {
          stats.credits += (subjectCredits || 0);
        }

        if (data.isCustomSubject && data.customSubjectType) {
          const customType = data.customSubjectType.trim();
          stats.customTypes[customType] = (stats.customTypes[customType] || 0) + 1;
          stats.allCustomTypes.set(customType, data.customSubjectType);
        } 
        else if (data.isLanguage) {
          stats.languages++;
        }
        else if (data.isLab) {
          stats.labs++;
        } 
        else if (data.isElective) {
          stats.electives++;
        } 
        else if (data.isProject) {
          stats.projects++;
        } 
        else {
          stats.core++;
        }
      });
    });

    // Dynamically build stat cards
    const statCards = [
      {
        id: 'total',
        label: 'Total Subjects',
        value: stats.total,
        icon: FiDatabase,
        gradient: 'from-blue-500 to-cyan-500',
        bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
        borderColor: 'border-blue-200'
      }
    ];

    if (stats.credits > 0) {
      statCards.push({
        id: 'credits',
        label: 'Total Credits',
        value: stats.credits,
        icon: FiCreditCard,
        gradient: 'from-emerald-500 to-green-500',
        bgColor: 'bg-gradient-to-br from-emerald-50 to-green-50',
        borderColor: 'border-emerald-200'
      });
    }

    if (stats.core > 0) {
      statCards.push({
        id: 'core',
        label: 'Core Subjects',
        value: stats.core,
        icon: FiBook,
        gradient: 'from-indigo-500 to-purple-500',
        bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
        borderColor: 'border-indigo-200'
      });
    }

    if (stats.labs > 0) {
      statCards.push({
        id: 'labs',
        label: 'Lab Subjects',
        value: stats.labs,
        icon: FiCpu,
        gradient: 'from-violet-500 to-purple-500',
        bgColor: 'bg-gradient-to-br from-violet-50 to-purple-50',
        borderColor: 'border-violet-200'
      });
    }

    if (stats.electives > 0) {
      statCards.push({
        id: 'electives',
        label: 'Electives',
        value: stats.electives,
        icon: FiUsers,
        gradient: 'from-amber-500 to-orange-500',
        bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
        borderColor: 'border-amber-200'
      });
    }

    if (stats.projects > 0) {
      statCards.push({
        id: 'projects',
        label: 'Project Work',
        value: stats.projects,
        icon: FiTarget,
        gradient: 'from-pink-500 to-rose-500',
        bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
        borderColor: 'border-pink-200'
      });
    }

    if (stats.languages > 0) {
      statCards.push({
        id: 'languages',
        label: 'Languages',
        value: stats.languages,
        icon: FiGlobe,
        gradient: 'from-cyan-500 to-blue-500',
        bgColor: 'bg-gradient-to-br from-cyan-50 to-blue-50',
        borderColor: 'border-cyan-200'
      });
    }

    Array.from(stats.allCustomTypes.keys()).forEach((type) => {
      const count = stats.customTypes[type] || 0;
      const actualName = stats.allCustomTypes.get(type) || type;
      
      if (count > 0) {
        statCards.push({
          id: `custom-${type.toLowerCase().replace(/\s+/g, '-')}`,
          label: actualName,
          value: count,
          icon: FiPackage,
          gradient: 'from-teal-500 to-emerald-500',
          bgColor: 'bg-gradient-to-br from-teal-50 to-emerald-50',
          borderColor: 'border-teal-200',
          isCustom: true
        });
      }
    });

    // Build mapping summary items dynamically
    const mappingSummaryItems = [];
    
    if (stats.core > 0) {
      mappingSummaryItems.push({
        label: 'Core',
        value: stats.core,
        color: 'blue',
        percentage: Math.round((stats.core / stats.total) * 100)
      });
    }
    
    if (stats.labs > 0) {
      mappingSummaryItems.push({
        label: 'Lab',
        value: stats.labs,
        color: 'violet',
        percentage: Math.round((stats.labs / stats.total) * 100)
      });
    }
    
    if (stats.electives > 0) {
      mappingSummaryItems.push({
        label: 'Elective',
        value: stats.electives,
        color: 'amber',
        percentage: Math.round((stats.electives / stats.total) * 100)
      });
    }
    
    if (stats.projects > 0) {
      mappingSummaryItems.push({
        label: 'Project',
        value: stats.projects,
        color: 'pink',
        percentage: Math.round((stats.projects / stats.total) * 100)
      });
    }
    
    if (stats.languages > 0) {
      mappingSummaryItems.push({
        label: 'Language',
        value: stats.languages,
        color: 'cyan',
        percentage: Math.round((stats.languages / stats.total) * 100)
      });
    }
    
    Array.from(stats.allCustomTypes.keys()).forEach((type) => {
      const count = stats.customTypes[type] || 0;
      if (count > 0) {
        mappingSummaryItems.push({
          label: type,
          value: count,
          color: 'teal',
          percentage: Math.round((count / stats.total) * 100),
          isCustom: true
        });
      }
    });

    return { stats, statCards, mappingSummaryItems };
  }, [fetchedMapping]);

  // Filter subjects based on search and filter
  const filteredDepartmentSubjects = useMemo(() => {
    if (!fetchedMapping?.departmentSubjects) return [];
    
    return fetchedMapping.departmentSubjects.map(dept => {
      const filteredSubjects = dept.subjects?.filter(subject => {
        const data = getSubjectData(subject);
        const matchesSearch = searchTerm === '' || 
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesFilter = subjectFilter === 'all' ||
          (subjectFilter === 'core' && !data.isLab && !data.isElective && !data.isProject && !data.isLanguage && !data.isCustomSubject) ||
          (subjectFilter === 'lab' && data.isLab) ||
          (subjectFilter === 'elective' && data.isElective) ||
          (subjectFilter === 'project' && data.isProject) ||
          (subjectFilter === 'language' && data.isLanguage) ||
          (subjectFilter === 'custom' && data.isCustomSubject);
        
        return matchesSearch && matchesFilter;
      });
      
      return { ...dept, subjects: filteredSubjects };
    }).filter(dept => dept.subjects?.length > 0);
  }, [fetchedMapping, searchTerm, subjectFilter]);

  const academicContext = useMemo(() => ({
    programName: resolveProgramName,
    programType: resolveProgramType,
    academicYear: academicYearName || (typeof fetchedMapping?.academicYearId === 'object' ? fetchedMapping.academicYearId.name : 'Academic Year'),
    context: contextDisplay,
    systemType: academicYearDetails?.isSemesterBased ? 'Semester System' : 'Term System'
  }), [resolveProgramName, resolveProgramType, academicYearName, fetchedMapping, contextDisplay, academicYearDetails]);

  // Calculate department stats
  const getDepartmentStats = () => {
    if (!fetchedMapping?.departmentSubjects) return [];
    
    return fetchedMapping.departmentSubjects.map((dept, index) => {
      const credits = dept.subjects?.reduce((sum, s) => sum + ((s.credits || (s.subjectId?.credits) || 0)), 0);
      const subjectCount = dept.subjects?.length || 0;
      
      const types = {
        core: 0,
        lab: 0,
        elective: 0,
        project: 0,
        language: 0,
        custom: {}
      };
      
      dept.subjects?.forEach(sub => {
        const data = getSubjectData(sub);
        
        if (data.isCustomSubject && data.customSubjectType) {
          const customType = data.customSubjectType.trim();
          types.custom[customType] = (types.custom[customType] || 0) + 1;
        } 
        else if (data.isLanguage) {
          types.language++;
        }
        else if (data.isLab) {
          types.lab++;
        } 
        else if (data.isElective) {
          types.elective++;
        } 
        else if (data.isProject) {
          types.project++;
        } 
        else {
          types.core++;
        }
      });
      
      return {
        name: dept.departmentId?.name || `Department ${index + 1}`,
        credits,
        subjectCount,
        types,
        index,
        hasCredits: credits > 0
      };
    });
  };

  const departmentStats = getDepartmentStats();

  // Loading State
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="relative mx-auto w-20 h-20">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full opacity-20"></div>
            <div className="absolute inset-4 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-6 text-gray-700 font-medium text-lg">Loading mapping details...</p>
          <p className="text-gray-500 mt-2">Please wait while we fetch all the data</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !fetchedMapping) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="p-4 bg-gradient-to-r from-red-100 to-pink-100 rounded-2xl inline-flex mb-6">
            <FiAlertCircle className="w-16 h-16 text-red-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Unable to Load Details</h3>
          <p className="text-gray-600 mb-8">{error}</p>
          <button 
            onClick={fetchMapping}
            className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            <FiRefreshCw className="w-5 h-5" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      {/* Header with Stats */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8 text-white">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <FiBookOpen className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-1 opacity-90">{academicContext.programType}</div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      {academicContext.programName}
                    </h2>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm">
                    <FiCalendar className="w-4 h-4" />
                    <span className="font-semibold">AY {academicContext.academicYear}</span>
                  </div>
                  
                  {academicContext.context && (
                    <div className={`flex items-center gap-2 ${academicContext.context.badgeClass} px-4 py-2 rounded-xl backdrop-blur-sm`}>
                      <academicContext.context.icon className="w-4 h-4" />
                      <span className="font-semibold">{academicContext.context.name}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm">
                    <FiClock className="w-4 h-4" />
                    <span className="font-semibold">Created: {formatCreatedDate}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm">
                    <FiAward className="w-4 h-4" />
                    <span className="font-semibold">{academicContext.systemType}</span>
                  </div>
                  
                  {isReadOnly && (
                    <div className="flex items-center gap-2 bg-amber-500 px-4 py-2 rounded-xl">
                      <FiLock className="w-4 h-4" />
                      <span className="font-semibold">View Only</span>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {statCards.length > 0 && (
          <div className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {statCards.map((card) => (
                <div 
                  key={card.id} 
                  className={`bg-white rounded-xl p-5 border ${card.borderColor} shadow-sm hover:shadow-lg transition-all duration-300`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2 ${card.bgColor} rounded-lg`}>
                      <card.icon className="w-6 h-6 text-gray-700" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500">
                      {card.label}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                  <div className={`h-1 w-full bg-gradient-to-r ${card.gradient} rounded-full mt-2`}></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search subjects by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              />
            </div>
          </div>
          <div className="md:w-64">
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
            >
              <option value="all">All Subject Types</option>
              <option value="core">Core Subjects</option>
              <option value="lab">Lab Subjects</option>
              <option value="elective">Electives</option>
              <option value="project">Projects</option>
              <option value="language">Languages</option>
              <option value="custom">Custom Subjects</option>
            </select>
          </div>
          <button
            onClick={toggleAllDepartments}
            className="px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300"
          >
            {expandedDepartments.size === fetchedMapping.departmentSubjects?.length ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
        {searchTerm && (
          <div className="mt-3 text-sm text-gray-600">
            Found {filteredDepartmentSubjects.reduce((total, dept) => total + (dept.subjects?.length || 0), 0)} subjects matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Departments Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg">
              <FiLayers className="w-6 h-6 text-white" />
            </div>
            <span>
              Departments ({fetchedMapping.departmentSubjects?.length})
              <span className="text-sm text-gray-600 font-normal ml-2">
                • {stats.total} total subjects • {stats.credits} total credits
              </span>
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <FiDownload className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <FiShare2 className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <FiCopy className="w-5 h-5" />
            </button>
          </div>
        </div>

        {fetchedMapping.departmentSubjects?.map((deptSubj, index) => {
          const isExpanded = expandedDepartments.has(index);
          const deptStat = departmentStats[index];
          const filteredSubjects = filteredDepartmentSubjects.find(f => 
            f.departmentId?._id === deptSubj.departmentId?._id
          )?.subjects || deptSubj.subjects;

          return (
            <div
              key={index}
              className={`bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 overflow-hidden ${
                isExpanded 
                  ? 'border-blue-300 shadow-xl' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
              }`}
            >
              {/* Department Header */}
              <div
                onClick={() => toggleDepartment(index)}
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl transition-all duration-300 ${
                      isExpanded 
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500' 
                        : 'bg-gradient-to-r from-gray-100 to-gray-200'
                    }`}>
                      {isExpanded ? (
                        <FiLayers className="w-6 h-6 text-white" />
                      ) : (
                        <FiLayers className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-xl font-bold text-gray-900">
                          {deptSubj.departmentId?.name || `Department ${index + 1}`}
                        </h4>
                        <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 font-medium rounded-full">
                          {deptStat?.subjectCount || 0} subjects
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {deptStat?.hasCredits && (
                          <span className="flex items-center gap-2">
                            <FiCreditCard className="w-4 h-4" />
                            {deptStat.credits} credits
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          {deptStat?.types.core > 0 && (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              {deptStat.types.core} Core
                            </span>
                          )}
                          {deptStat?.types.lab > 0 && (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                              {deptStat.types.lab} Lab
                            </span>
                          )}
                          {deptStat?.types.language > 0 && (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                              {deptStat.types.language} Language
                            </span>
                          )}
                          {Object.keys(deptStat?.types.custom || {}).length > 0 && (
                            <span className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                              Custom
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <FiChevronUp className="w-6 h-6 text-gray-400" />
                    ) : (
                      <FiChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Subjects List */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  <div className="p-6">
                    {filteredSubjects && filteredSubjects.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredSubjects.map((subject, sIdx) => {
                          const data = getSubjectData(subject);
                          const typeInfo = getSubjectTypeInfo(subject);
                          const credits = subject.credits || data.credits;

                          return (
                            <div
                              key={sIdx}
                              className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 p-4 hover:border-blue-200 hover:shadow-md transition-all duration-300"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h5 className="font-bold text-gray-900 text-base">
                                      {data.name}
                                    </h5>
                                    {typeInfo && (
                                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-bold ${typeInfo.badgeClass}`}>
                                        <typeInfo.icon className="w-3 h-3" />
                                        {typeInfo.label}
                                      </span>
                                    )}
                                  </div>
                                  {data.subjectCode && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                      <FiCode className="w-3 h-3" />
                                      <code className="font-mono">{data.subjectCode}</code>
                                    </div>
                                  )}
                                </div>
                                {credits > 0 && (
                                  <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-50 to-green-50 px-3 py-2 rounded-lg border border-emerald-200">
                                    <FiCreditCard className="w-4 h-4 text-emerald-600" />
                                    <span className="text-sm font-bold text-emerald-700">{credits} CR</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="pt-3 border-t border-gray-100">
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <div className="flex items-center gap-3">
                                    {data.isMandatory && (
                                      <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                        <FiCheckCircle className="w-3 h-3" />
                                        Mandatory
                                      </span>
                                    )}
                                    {data.isCreditBased && (
                                      <span className="text-blue-600 font-medium">Credit Based</span>
                                    )}
                                  </div>
                                  {data.totalHours > 0 && (
                                    <span className="font-medium">{data.totalHours} hours</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No subjects found matching your search criteria</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex-1">
            <h4 className="text-lg font-bold text-gray-900 mb-3">Mapping Summary</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-gray-600">subjects across {fetchedMapping.departmentSubjects?.length} departments</div>
              </div>
              {stats.credits > 0 && (
                <div className="text-gray-700">
                  Total credits: <span className="font-bold text-emerald-700">{stats.credits}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                {mappingSummaryItems.map((item) => (
                  <div key={item.label} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200">
                    <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                    <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                    <span className="text-sm font-bold text-gray-900">{item.value}</span>
                    <span className="text-xs text-gray-500">({item.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Close View
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewMappedSubjects;